import { useSearchBuddyData } from '@/components/mobileApp/mobileHooks/useSearchBuddyData';
import { environmentRecoil } from '@/recoil/atoms/MobileRecoil/MobileRecoil';
import { PASTEL_COLORS } from '@/shared/constants';
import { TelHyphenWithStarHash } from '@/shared/util/format/phoneNumberFormatUtil';
import React, { useEffect, useRef, useState } from 'react';
import { IoMdPerson } from 'react-icons/io';
import { useRecoilState, useRecoilValue } from 'recoil';
import Keypad from './sip-components/Keypad';
import StatusDisplay from './sip-components/StatusDisplay';
import { callStatusRecoil, incomingCallInfoRecoil, phoneNumberRecoil } from './sip-recoil/sipRecoil';
import { endCall, getCurrentSession, makeCall } from './siphook/sipService';
import useRingtone from './siphook/useRingtone';
import * as S from './sipstyles/SipClient.style';

const SipClient = () => {
  const [callTime, setCallTime] = useState(0); // 통화 시간 (초)
  const [phoneNumber, setPhoneNumber] = useRecoilState(phoneNumberRecoil); // 입력한 전화번호
  const [debouncePhoneNumber, setDebouncePhoneNumber] = useState<string>('');
  const [callStatus, setCallStatus] = useRecoilState(callStatusRecoil); // idle, dialing, connected, ended, ringing
  // const [sipStatus, setSipStatus] = useState<'connected' | 'disconnected' | null>(null);
  const [incomingCallInfo, setIncomingCallInfo] = useRecoilState(incomingCallInfoRecoil); // 수신 전화 정보

  const phoneInputRef = useRef<HTMLInputElement>(null); // PhoneDisplay에 대한 ref 추가

  const environmentS = useRecoilValue(environmentRecoil);

  // const connectionInfo = queryClient.getQueryData(['/sip/connection-info']);
  // const { start, stop } = useSipClient({ setSipStatus, setIncomingCallInfo, setCallStatus }); // 훅 사용
  useRingtone({ callStatus, audioSrc: '/file/audioFile/incoming-call-bell.mp3' }); // 전화 걸려왔을 때 벨소리 Hook

  const pcEnvironment = environmentS === 'pc'; // 웹, 모바일 키패드 인풋의 사용성을 위한 변수

  // ---------------------------------------------------------------

  // useEffect(() => {
  //   if (!sipStatus && connectionInfo) {
  //     handleStart();
  //   }
  // }, [sipStatus]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (callStatus === 'connected') {
      timer = setInterval(() => {
        setCallTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timer) clearInterval(timer); // 타이머 초기화
      setCallTime(0);
    }

    return () => {
      if (timer) clearInterval(timer); // cleanup
    };
  }, [callStatus]);

  // const handleStart = () => {
  //   start(connectionInfo);
  // };

  // 통화 시간 포맷
  const formatCallTime = () => {
    const minutes = Math.floor(callTime / 60);
    const seconds = callTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleKeypad = (key: string) => {
    setPhoneNumber((prev) => prev + key);
  };

  // 통화 종료 이벤트시 2초간 대기 후 대기상태로 변경
  useEffect(() => {
    if (callStatus === 'ended') {
      // 1초 후 대기 상태로 변경 -> 종료하면 해당 상태값 변경으로 바꾸면 됌
      setTimeout(() => {
        setCallStatus('idle');
        setIncomingCallInfo(null); // 수신인 정보 초기화
      }, 2000);
    }
  }, [callStatus]);

  // 전화 걸기
  const handleDial = () => {
    if (phoneNumber) {
      makeCall(phoneNumber, setCallStatus);
      setDebouncePhoneNumber('');
    }
  };

  // 전화 받기
  const handleAcceptCall = () => {
    const session = getCurrentSession();

    if (session) {
      session.answer({
        mediaConstraints: { audio: true, video: false }, // 오디오만 활성화
      });

      const peerConnection = session.connection;
      const remoteStream = new MediaStream();

      peerConnection.ontrack = (event: any) => {
        event.streams[0].getTracks().forEach((track: any) => {
          remoteStream.addTrack(track);
        });

        const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement;
        if (remoteAudio) {
          remoteAudio.srcObject = remoteStream;
          remoteAudio.play().catch((err) => console.error('오디오 재생 오류:', err));
        }
      };

      setCallStatus('connected');
    }
    // 통화 연결 처리 로직 추가
  };

  // 전화 거절
  const handleRejectCall = () => {
    const session = getCurrentSession();
    if (session) {
      session.terminate();
      setCallStatus('ended');
    }
    // 통화 거절 처리 로직 추가
  };

  // 통화 종료
  const handleEndCall = () => {
    setCallStatus('ended');
    endCall();
    setPhoneNumber('');
  };

  const getStatusText = () => {
    if (callStatus === 'connected') {
      return incomingCallInfo ? '통화 중' : '전화 연결됨';
    }

    switch (callStatus) {
      // case 'idle':
      //   return '대기 중';
      case 'dialing':
        return '전화 중...';
      case 'ringing':
        return '수신 전화';
      case 'ended':
        return '통화 종료됨';
      default:
        return '';
    }
  };

  // 다이얼 번호 지우기
  const handleBackspace = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  // 다이얼 번호 전체 지우기
  const handleBackspaceAll = () => {
    setPhoneNumber('');
  };

  // Debounce effect 주소록 검색
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncePhoneNumber(phoneNumber.replace(/-/g, ''));
    }, 600);
    return () => {
      clearTimeout(handler);
    };
  }, [phoneNumber]);

  // 주소록 검색 훅
  const useSearchBuddyListHooks = useSearchBuddyData('', debouncePhoneNumber);

  // Scroll handler to fetch more data
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (useSearchBuddyListHooks.hasNextPage && !useSearchBuddyListHooks.isFetchingNextPage) {
        useSearchBuddyListHooks.fetchNextPage();
      }
    }
  };

  // 주소록 리스트로 다이얼 입력
  const onClickBuddyListItem = (buddy: string) => {
    setDebouncePhoneNumber('');
    setPhoneNumber(buddy);
  };

  return (
    <S.PageContainer
      onClick={() => {
        // PageContainer 클릭 시 PhoneDisplay에 포커스
        if (pcEnvironment) {
          phoneInputRef.current?.focus();
        }
      }}
    >
      {getStatusText() && (
        <StatusDisplay
          callTime={formatCallTime()}
          phoneNumber={TelHyphenWithStarHash(phoneNumber)}
          incomingCallInfo={incomingCallInfo}
          callStatus={callStatus}
        />
      )}

      {/* <S.SipButton disconnected={sipStatus === 'disconnected'} onClick={handleStart}>
        음성 서비스 연결
      </S.SipButton> */}

      <S.Container>
        <S.BuddyListContainer onScroll={handleScroll}>
          {useSearchBuddyListHooks.buddyListData.map((buddyList) => {
            const randomColor = PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];
            return (
              <S.BuddyListItem
                key={buddyList.buddySeqNo}
                onClick={() => {
                  onClickBuddyListItem(buddyList.keyCommNo || '');
                }}
              >
                <S.BuddyIcon bgColor={randomColor}>
                  <IoMdPerson />
                </S.BuddyIcon>
                <S.BuddyName>{buddyList.buddyNm}</S.BuddyName>
                <S.BuddyNumber>{buddyList.keyCommNo && TelHyphenWithStarHash(buddyList.keyCommNo)}</S.BuddyNumber>
              </S.BuddyListItem>
            );
          })}
        </S.BuddyListContainer>

        <S.PhoneContainer>
          <S.PhoneDisplay
            ref={phoneInputRef}
            value={TelHyphenWithStarHash(phoneNumber)}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleDial();
              }
            }}
            autoFocus={pcEnvironment}
            readOnly={!pcEnvironment}
          />

          <S.KeypadContainer>
            <Keypad
              phoneNumber={phoneNumber}
              callStatus={callStatus}
              onKeypad={handleKeypad}
              onDial={handleDial}
              onBackspace={handleBackspace}
              onBackspaceAll={handleBackspaceAll}
              onEndCall={handleEndCall}
              onRejectCall={handleRejectCall}
              onAcceptCall={handleAcceptCall}
            />
          </S.KeypadContainer>
        </S.PhoneContainer>

        <S.AudioElement id="remoteAudio" autoPlay />
      </S.Container>
    </S.PageContainer>
  );
};

export default SipClient;
